import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { cardSx, colors, typography } from '../constants';
import { CenteredLoader, ScreenState, ShareBar } from '../components';
import { useAnalytics, useMenuItemsPages } from '../hooks/useProfitData';
import { useDateRange } from '../hooks/useSelection';
import {
  dateRangeLabel,
  fmt,
  fmtD,
  fmtN,
  negativeTone,
  pct,
} from '../lib/format';
import { netColor } from './Overview';

/**
 * The items of one category, fetched when it is opened.
 *
 * They used to be filtered out of the dashboard's item list, which meant the
 * whole menu had to be in it for the sake of the one category a reader expands.
 * The list is paged on the server now, so this asks for that category alone.
 */
function CategoryItems({ category }) {
  const list = useMenuItemsPages({ category });
  const items = list.data
    ? list.data.pages.flatMap((page) => page.items)
    : [];

  if (list.isPending) {
    return <CenteredLoader label="Loading items…" />;
  }

  return (
    <>
      {items.map((item, idx) => (
        <Box
          key={item.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1,
            gap: 1,
            borderBottom:
              idx < items.length - 1 ? `1px solid ${colors.border}` : 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: typography.emphasis }}>
              {item.emoji}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: typography.caption, fontWeight: 600 }}
              >
                {item.name}
              </Typography>
              <Typography
                sx={{ fontSize: typography.caption, color: colors.faint }}
              >
                {fmtN(item.unitsSold)} sold · {fmtD(item.price)} · cost{' '}
                {fmtD(item.fcd)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography
              sx={{
                fontSize: typography.caption,
                fontWeight: 700,
                color: netColor(item.netProfitPct),
              }}
            >
              {pct(item.netProfitPct)}
            </Typography>
            <Typography
              sx={{ fontSize: typography.caption, color: colors.faint }}
            >
              {fmt(item.totalProfit)}
            </Typography>
          </Box>
        </Box>
      ))}

      {list.hasNextPage ? (
        <Box
          component="button"
          type="button"
          disabled={list.isFetchingNextPage}
          onClick={() => list.fetchNextPage()}
          sx={{
            width: '100%',
            mt: 1,
            py: 1,
            cursor: list.isFetchingNextPage ? 'default' : 'pointer',
            fontFamily: 'inherit',
            fontSize: typography.caption,
            fontWeight: 600,
            color: colors.primary,
            backgroundColor: 'transparent',
            border: `1px solid ${colors.border}`,
            borderRadius: 2,
          }}
        >
          {list.isFetchingNextPage ? 'Loading…' : '+ Load more items'}
        </Box>
      ) : null}
    </>
  );
}

function MiniTile({ label, value, color }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 120,
        backgroundColor: colors.card,
        borderRadius: 2,
        py: 1,
        px: 1.25,
        border: `1px solid ${colors.border}`,
      }}
    >
      <Typography
        sx={{
          fontSize: typography.caption,
          color: colors.faint,
          fontWeight: 600,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: typography.small,
          fontWeight: 800,
          color,
          mt: 0.25,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function Categories() {
  const { range, customLabel, month } = useDateRange();
  const query = useAnalytics();
  const [expanded, setExpanded] = useState(null);

  const dateLabel = dateRangeLabel(range, month, customLabel);

  // Folded by the engine over every item that sold, not by this screen over the
  // page of them the response carries.
  const cats = query.data?.itemCategories ?? [];
  const maxRev = cats[0]?.revenue || 1;

  return (
    <ScreenState
      id="categories"
      query={query}
      loadingLabel="Loading categories…"
      allowNoData
    >
      {(data) => {
        const totalRevenue = data.totalRevenue || 1;
        return (
          <>
            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.mutedForeground,
                mb: 0.5,
              }}
            >
              Revenue &amp; profit by category · {dateLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.faint,
                mb: 1.75,
              }}
            >
              Open any category to see its items.
            </Typography>

            {cats.map((cat) => {
              const marginPct =
                cat.revenue > 0 ? (cat.profit / cat.revenue) * 100 : 0;
              const mc = netColor(marginPct);
              const isExp = expanded === cat.name;

              return (
                <Box key={cat.name} sx={{ ...cardSx, mb: 1.25, overflow: 'hidden' }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setExpanded(isExp ? null : cat.name)}
                    sx={{
                      width: '100%',
                      p: 2,
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                      '&:hover': { backgroundColor: colors.muted },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1.5,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{ fontWeight: 700, fontSize: typography.small }}
                        >
                          {cat.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: typography.caption,
                            color: colors.mutedForeground,
                            mt: '1px',
                          }}
                        >
                          {cat.variants} variant{cat.variants !== 1 ? 's' : ''} ·{' '}
                          {fmtN(cat.units)} sold
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Typography
                          sx={{ fontSize: typography.body, fontWeight: 800 }}
                        >
                          {fmt(cat.revenue)}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: typography.caption,
                            fontWeight: 700,
                            color: mc,
                            mt: '1px',
                            ...negativeTone(marginPct),
                          }}
                        >
                          {fmt(cat.profit)} profit · {pct(marginPct)}
                        </Typography>
                      </Box>
                      {isExp ? (
                        <ExpandLessIcon
                          sx={{ fontSize: 18, color: colors.primary, mt: 0.25 }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          sx={{ fontSize: 18, color: colors.faint, mt: 0.25 }}
                        />
                      )}
                    </Box>

                    {/* The bar is relative to the biggest category, so the
                        ranking reads at a glance even when one dominates. */}
                    <ShareBar
                      height={5}
                      width={Math.round((cat.revenue / maxRev) * 100)}
                      sx={{ mt: 1.25 }}
                    />
                    <Typography
                      sx={{
                        fontSize: typography.caption,
                        color: colors.faint,
                        mt: 0.4,
                      }}
                    >
                      {pct((cat.revenue / totalRevenue) * 100)} of total revenue
                    </Typography>
                  </Box>

                  {isExp ? (
                    <Box
                      sx={{
                        borderTop: `1px solid ${colors.border}`,
                        backgroundColor: colors.muted,
                        px: 2,
                        pt: 1.25,
                        pb: 1.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          mb: 1.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        <MiniTile
                          label="Revenue"
                          value={fmt(cat.revenue)}
                          color={colors.foreground}
                        />
                        <MiniTile
                          label="Est. Cost"
                          value={fmt(cat.cost)}
                          color={colors.mutedForeground}
                        />
                        <MiniTile
                          label="Est. Profit"
                          value={fmt(cat.profit)}
                          color={mc}
                        />
                      </Box>

                      <Typography
                        sx={{
                          fontSize: typography.caption,
                          fontWeight: 700,
                          color: colors.mutedForeground,
                          mb: 1,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Items in this category
                      </Typography>

                      <CategoryItems category={cat.name} />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </>
        );
      }}
    </ScreenState>
  );
}
